import Phaser from 'phaser';
import { audioManager } from '../core/CoreManagerAudio';
import { ParticleData } from '../data/ParticleData';
import { isLowQuality } from '../core/QualitySettings';
import { LayerDepths } from '../constants/enums';

/**
 * Configuration for a single visual effect
 */
export interface EffectConfig {
  /** Texture/sprite key for the effect */
  sprite?: string;

  /** Specific animation to play (optional, defaults to sprite_Default) */
  animation?: string;

  /** Blend mode: 'normal', 'additive', or 'multiply' */
  blendMode?: 'normal' | 'additive' | 'multiply';

  /** Duration in seconds for fade out (effect destroyed after fade) */
  fadeOut?: number;

  /** Scale of the sprite */
  scale?: number;

  /** Sound to play when effect spawns */
  sound?: string;

  /** Camera shake configuration */
  shake?: { intensity: number; duration: number };

  /** Particle system key (future extension) */
  particles?: string;
  /** Optional particle burst count override */
  particleCount?: number;

  /** Color tint to apply (hex number) */
  tint?: number;
}

/**
 * Effects configuration for projectiles
 */
export interface ProjectileEffects {
  /** Effect when hitting an entity */
  onHit?: EffectConfig;

  /** Effect when hitting floor/walls (can differ from onHit) */
  onHitFloor?: EffectConfig;

  /** Effect when projectile expires without hitting */
  onExpire?: EffectConfig;

  /** Per-material overrides (future extension) */
  onHitMaterial?: Record<string, Partial<EffectConfig>>;
}

/**
 * Regex to parse #category.id references
 * Matches: #animations.laserexplotion, #particles.musicalnote, etc.
 */
const REFERENCE_REGEX = /^#(projectiles|weapons|animations|entities|particles|sounds)\.([a-z0-9_]+)$/i;

/**
 * EffectsSystem - Data-driven effect spawning
 *
 * Spawns visual effects based on EffectConfig definitions.
 * Each weapon/projectile defines its own effects in its data,
 * and this system simply executes them.
 *
 * Usage:
 *   const fx = projectileDefinition.effects?.onHit;
 *   if (fx) effectsSystem.spawn(fx, x, y);
 */
export class EffectsSystem {
  private scene: Phaser.Scene;
  private activeEffects: Set<Phaser.GameObjects.Sprite> = new Set();
  private activeTimeouts: number[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    // Limpieza total al reiniciar/cerrar la escena
    this.scene.events.once('shutdown', this.destroyAll, this);
    this.scene.events.once('destroy', this.destroyAll, this);
  }

  /**
   * Resolve a #category.id reference to the actual asset key.
   * Example: "#animations.laserexplotion" -> "laserexplotion"
   * If not a reference, returns the original string lowercased.
   */
  private resolveReference(ref: string): string {
    if (!ref.startsWith('#')) {
      return ref.toLowerCase();
    }
    const match = ref.match(REFERENCE_REGEX);
    if (!match) {
      console.warn(`[EffectsSystem] Invalid reference format: "${ref}". Expected #category.id`);
      return ref.toLowerCase();
    }
    // Return the id part (match[2]), lowercased
    return match[2].toLowerCase();
  }

  /**
   * Spawn an effect at the given position
   * @param config Effect configuration from projectile/weapon data
   * @param x World X position
   * @param y World Y position
   * @returns The created sprite, or null if no sprite was created
   */
  spawn(config: EffectConfig, x: number, y: number): Phaser.GameObjects.Sprite | null {
    let effect: Phaser.GameObjects.Sprite | null = null;

    // Create sprite if defined
    if (config.sprite) {
      // Resolve #category.id reference to actual texture key
      const textureKey = this.resolveReference(config.sprite);

      // Verify texture exists
      if (!this.scene.textures.exists(textureKey)) {
        console.warn(`[EffectsSystem] Missing texture: ${textureKey}`);
      } else {
        // Create sprite
        effect = this.scene.add.sprite(x, y, textureKey);
        effect.setDepth(LayerDepths.Effects);
        effect.setData('effect', true);
        this.trackEffect(effect);

        // Apply blend mode
        if (config.blendMode === 'additive') {
          effect.setBlendMode(Phaser.BlendModes.ADD);
        } else if (config.blendMode === 'multiply') {
          effect.setBlendMode(Phaser.BlendModes.MULTIPLY);
        }

        // Apply scale
        if (config.scale) {
          effect.setScale(config.scale);
        }

        // Apply tint
        if (config.tint !== undefined) {
          effect.setTint(config.tint);
        }

        // Reproducir animación (usa textureKey para el default)
        const animKey = config.animation
          ? this.resolveReference(config.animation)
          : `${textureKey}_default`;
        if (this.scene.anims.exists(animKey)) {
          effect.play(animKey);
          // Asegurar limpieza cuando termina la animación
          effect.once('animationcomplete', () => effect?.destroy());
        }

        // Fallback: destruir igual después de un tiempo si la animación no corre
        if (!config.fadeOut) {
          this.scene.time.delayedCall(900, () => {
            if (effect && effect.active) {
              effect.destroy();
            }
          });
        }

        // Fade out and destroy
        if (config.fadeOut) {
          this.scene.tweens.add({
            targets: effect,
            alpha: 0,
            duration: config.fadeOut * 1000,
            ease: 'Linear',
            onComplete: () => effect?.destroy(),
          });
        }

        // Seguro anti-bugs: si por alguna razón no se limpia, destruimos igual
        const safetyMs = Math.max(900, (config.fadeOut ? config.fadeOut * 1000 : 0) + 200);
        this.scene.time.delayedCall(safetyMs, () => {
          if (effect && effect.active) {
            effect.destroy();
          }
        });

        // Fallback extra por si el Scene/Time está pausado (usa reloj real)
        if (typeof window !== 'undefined') {
          const timeoutId = window.setTimeout(() => {
            if (effect && effect.active) {
              effect.destroy();
            }
          }, safetyMs + 200);
          this.activeTimeouts.push(timeoutId);
        }
      }
    }

    // Play sound via audioManager
    if (config.sound) {
      audioManager.playSfx(config.sound, 'explosion', this.scene);
    }

    // Shake camera
    if (config.shake) {
      this.scene.cameras.main.shake(config.shake.duration, config.shake.intensity);
    }

    // Spawn particles if defined (skip on low quality)
    if (config.particles && !isLowQuality()) {
      const particleKey = this.resolveReference(config.particles);
      this.spawnParticleBurst(particleKey, x, y, 270, config.particleCount); // 270 = upward
    }

    return effect;
  }

  private trackEffect(effect: Phaser.GameObjects.Sprite): void {
    this.activeEffects.add(effect);
    effect.once(Phaser.GameObjects.Events.DESTROY, () => {
      this.activeEffects.delete(effect);
    });
  }

  private destroyAll(): void {
    for (const effect of this.activeEffects) {
      if (effect.active) {
        effect.destroy();
      }
    }
    this.activeEffects.clear();

    for (const id of this.activeTimeouts) {
      if (typeof window !== 'undefined') {
        window.clearTimeout(id);
      }
    }
    this.activeTimeouts = [];
  }

  /**
   * Spawn an effect from ProjectileEffects based on event type
   * @param effects ProjectileEffects configuration
   * @param event 'onHit' | 'onHitFloor' | 'onExpire'
   * @param x World X position
   * @param y World Y position
   * @param material Optional material for per-material effects
   */
  spawnFromEvent(
    effects: ProjectileEffects | undefined,
    event: 'onHit' | 'onHitFloor' | 'onExpire',
    x: number,
    y: number,
    material?: string,
    allowParticles: boolean = true
  ): Phaser.GameObjects.Sprite | null {
    if (!effects) return null;

    // Get base effect config for this event
    let config = effects[event];

    // Fallback: onHitFloor -> onHit
    if (!config && event === 'onHitFloor') {
      config = effects.onHit;
    }

    if (!config) return null;

    // Apply material overrides if available
    if (material && effects.onHitMaterial?.[material]) {
      config = { ...config, ...effects.onHitMaterial[material] };
    }

    if (!allowParticles) {
      config = { ...config };
      delete config.particles;
      delete config.particleCount;
    }

    return this.spawn(config, x, y);
  }

  /**
   * Spawn a particle burst effect at the given position
   *
   * @param name Particle definition name (e.g., "Particles5", "particleHay")
   * @param x World X position
   * @param y World Y position
   * @param angle Emission angle in degrees (default: 270 = upward)
   * @param count Number of particles (default: use definition rate)
   * @returns The particle emitter, or null if not found
   */
  spawnParticleBurst(
    name: string,
    x: number,
    y: number,
    angle: number = 270,
    count?: number
  ): Phaser.GameObjects.Particles.ParticleEmitter | null {
    return ParticleData.burst(this.scene, name, x, y, angle, count);
  }

  /**
   * Create a continuous particle emitter at the given position
   *
   * @param name Particle definition name
   * @param x Initial X position
   * @param y Initial Y position
   * @param angle Emission angle in degrees (default: 270 = upward)
   * @returns The particle emitter, or null if not found
   */
  createParticleEmitter(
    name: string,
    x: number,
    y: number,
    angle: number = 270
  ): Phaser.GameObjects.Particles.ParticleEmitter | null {
    return ParticleData.createEmitter(this.scene, name, x, y, angle);
  }
}
